import * as React from 'react';
import { format, parse, isValid } from 'date-fns';

export const DISPLAY_FORMAT = 'dd/MM/yyyy';
export const ISO_FORMAT = 'yyyy-MM-dd';

/** Approximate rendered calendar popover box, for flip/clamp math. */
const CALENDAR_MAX_HEIGHT = 360;
const CALENDAR_APPROX_WIDTH = 300;

/**
 * Close the calendar when a mousedown lands outside both the anchor and the
 * portaled popover (same pattern as Select). Pass the stable `setOpen` from
 * `useState` so the listener only re-subscribes when `open` flips.
 */
export function useCloseOnOutsideClick(
  open: boolean,
  containerRef: React.RefObject<HTMLElement | null>,
  popoverRef: React.RefObject<HTMLElement | null>,
  setOpen: (open: boolean) => void,
) {
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        popoverRef.current &&
        !popoverRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, containerRef, popoverRef, setOpen]);
}

/**
 * Fixed-position style for a calendar popover portaled to `document.body`, so
 * it escapes any `overflow` clipping ancestor (e.g. a scrollable modal dialog).
 * Recomputed from the anchor's viewport rect on open; flips above when the
 * space below is too small, and clamps horizontally to the viewport.
 * Mirrors the Select dropdown positioning.
 *
 * `size` overrides the assumed panel box for the flip/clamp math — pass it for
 * non-calendar panels (ColorPicker's is narrower and shorter) so they don't
 * flip above or shift left earlier than they need to.
 */
export function useCalendarPopoverStyle(
  open: boolean,
  anchorRef: React.RefObject<HTMLElement | null>,
  size: { width?: number; height?: number } = {},
): React.CSSProperties {
  const { width = CALENDAR_APPROX_WIDTH, height = CALENDAR_MAX_HEIGHT } = size;
  const [style, setStyle] = React.useState<React.CSSProperties>({
    position: 'fixed',
    top: 0,
    left: 0,
    visibility: 'hidden',
  });

  React.useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const gap = 4;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const placeAbove = spaceBelow < height && spaceAbove > spaceBelow;
    const left = Math.max(
      8,
      Math.min(rect.left, window.innerWidth - width - 8),
    );

    setStyle({
      position: 'fixed',
      ...(placeAbove
        ? { bottom: window.innerHeight - rect.top + gap }
        : { top: rect.bottom + gap }),
      left,
      zIndex: 9999,
      // Override the `pointer-events:none` a modal dialog sets on <body>
      // (Radix DismissableLayer) so the portaled calendar stays clickable.
      pointerEvents: 'auto',
    });
  }, [open, anchorRef, width, height]);

  return style;
}

export function parseIsoDate(value: string | undefined): Date | undefined {
  if (!value) return undefined;
  const date = parse(value, ISO_FORMAT, new Date());
  if (!isValid(date)) return undefined;
  return format(date, ISO_FORMAT) === value ? date : undefined;
}

export function parseInputDate(value: string): Date | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  // ISO format — strict roundtrip (unambiguous)
  const isoDate = parse(trimmed, ISO_FORMAT, new Date());
  if (isValid(isoDate) && format(isoDate, ISO_FORMAT) === trimmed) {
    return isoDate;
  }

  // Normalize separators: accept dots and dashes as well as slashes
  const normalized = trimmed.replace(/[.-]/g, '/');

  // Pad single-digit day/month: 1/3/2026 → 01/03/2026
  const parts = normalized.split('/');
  if (parts.length === 3) {
    const [day, month, year] = parts;
    const padded = `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
    const displayDate = parse(padded, DISPLAY_FORMAT, new Date());
    if (
      isValid(displayDate) &&
      format(displayDate, DISPLAY_FORMAT) === padded
    ) {
      return displayDate;
    }
  }

  return undefined;
}

/** dd/MM/yyyy digit mask applied while typing. */
export function maskDateInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  let masked = '';
  for (let i = 0; i < digits.length; i++) {
    if (i === 2 || i === 4) masked += '/';
    masked += digits[i];
  }
  return masked;
}

/** Size-agnostic base; always pair with a `dateInputSizeClassName` entry. */
export const dateInputClassName =
  'flex w-full rounded-xl border-2 border-border bg-muted font-body placeholder:text-muted-foreground transition-all duration-200 ease-spring hover:border-foreground/40 focus-visible:outline-none focus-visible:border-foreground focus-visible:shadow-hard disabled:cursor-not-allowed disabled:opacity-50';

/** Height/padding/type scale, matching the Select trigger sizes. */
export const dateInputSizeClassName = {
  default: 'h-12 px-4 py-2 text-base',
  sm: 'h-9 px-3 text-sm',
} as const;

export type DateInputSize = keyof typeof dateInputSizeClassName;

export const dateInputErrorClassName =
  'border-destructive focus-visible:border-destructive focus-visible:shadow-[4px_4px_0_hsl(var(--destructive))]';

/**
 * Dark "endpoint" day cell (selected day / range start / range end). The
 * modifier classes land on the `<td>`; the inner day button's own
 * `hover:bg-muted` would paint light gray under the inherited near-white
 * text (unreadable), so hover lightens the dark cell instead.
 */
export const selectedDayClassName =
  '!bg-foreground !text-background font-semibold rounded-lg [&>button:hover]:!bg-background/20';

export const calendarClassNames = {
  root: 'p-3',
  months: 'flex flex-col',
  month: 'space-y-3',
  month_caption: 'flex justify-center items-center h-7',
  caption_label: 'text-sm font-semibold',
  nav: 'flex items-center justify-between absolute inset-x-3 top-3',
  button_previous:
    'size-7 flex items-center justify-center rounded-lg border-2 border-border bg-muted hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer',
  button_next:
    'size-7 flex items-center justify-center rounded-lg border-2 border-border bg-muted hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer',
  weekdays: 'flex',
  weekday: 'w-9 text-center text-xs font-medium text-muted-foreground',
  week: 'flex',
  day: 'p-0',
  day_button:
    'size-9 flex items-center justify-center rounded-lg text-sm transition-colors hover:bg-muted focus-visible:outline-none cursor-pointer',
  selected: selectedDayClassName,
  // `!font-bold` so today stays bold when the cell is also a range endpoint
  // (whose `font-semibold` otherwise wins by stylesheet order).
  today: '!font-bold underline underline-offset-2 text-riso-pink',
  outside: 'text-muted-foreground/40',
  disabled: 'text-muted-foreground/30 cursor-not-allowed',
} as const;
